import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", zip: "", country: "US" });

  useEffect(() => {
    if (params.get("cancelled")) toast.error("Payment cancelled. Your cart is intact.");
  }, [params]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [promoInput, setPromoInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [promoError, setPromoError] = useState("");

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoError("");
    try {
      const { data } = await api.post("/discounts/validate", { code: promoInput.trim(), subtotal: total });
      setDiscount(data);
      toast.success(`Code ${data.code} applied`);
    } catch (err) {
      setDiscount(null);
      setPromoError(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, qty: i.qty, customization: i.customization })),
        customer: form,
        payment_method: method,
        discount_code: discount?.code || null,
      };
      const { data: order } = await api.post("/orders", payload);
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
        clear();
        window.location.href = data.checkout_url;
      } else {
        clear();
        navigate(`/payment/success?invoice=1&order=${order.order_number}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  if (!items.length && !loading) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="checkout-empty">
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Your cart is empty.</p>
        <button onClick={() => navigate("/shop")} className="btn-kinetic mt-8" data-testid="checkout-shop-button">Shop the drop</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-12 pt-32 pb-16" data-testid="checkout-page">
      <Reveal>
        <p className="eyebrow mb-3">Checkout</p>
        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Lock it in</h1>
        {!user && (
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500">
            Checking out as guest · <button onClick={() => navigate("/auth")} className="text-kinetic hover:underline" data-testid="checkout-login-link">login for order tracking</button>
          </p>
        )}
      </Reveal>

      <form onSubmit={submit} className="grid lg:grid-cols-5 gap-10 mt-12">
        <div className="lg:col-span-3 space-y-8">
          <div className="space-y-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-kinetic">01 // Contact & Shipping</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="checkout-name-input" required value={form.name} onChange={set("name")} placeholder="FULL NAME" className="input-dark font-mono uppercase text-xs tracking-widest" />
              <input data-testid="checkout-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="EMAIL" className="input-dark font-mono uppercase text-xs tracking-widest" />
            </div>
            <input data-testid="checkout-address-input" required value={form.address} onChange={set("address")} placeholder="STREET ADDRESS" className="input-dark font-mono uppercase text-xs tracking-widest" />
            <div className="grid sm:grid-cols-3 gap-4">
              <input data-testid="checkout-city-input" required value={form.city} onChange={set("city")} placeholder="CITY" className="input-dark font-mono uppercase text-xs tracking-widest" />
              <input data-testid="checkout-zip-input" required value={form.zip} onChange={set("zip")} placeholder="ZIP" className="input-dark font-mono uppercase text-xs tracking-widest" />
              <input data-testid="checkout-country-input" required value={form.country} onChange={set("country")} placeholder="COUNTRY" className="input-dark font-mono uppercase text-xs tracking-widest" />
            </div>
          </div>

          <div className="space-y-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-kinetic">02 // Payment</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                data-testid="payment-method-card"
                onClick={() => setMethod("card")}
                className={`border p-5 text-left transition-colors active:scale-95 ${method === "card" ? "border-kinetic bg-card" : "border-white/15 hover:border-white/40"}`}
              >
                <CreditCard size={18} className={method === "card" ? "text-kinetic" : "text-zinc-500"} />
                <p className="font-bold uppercase tracking-wide text-sm mt-3">Card — Instant</p>
                <p className="font-mono text-[11px] text-zinc-500 mt-1 uppercase tracking-widest">Secure Stripe checkout</p>
              </button>
              <button
                type="button"
                data-testid="payment-method-invoice"
                onClick={() => setMethod("invoice")}
                className={`border p-5 text-left transition-colors active:scale-95 ${method === "invoice" ? "border-kinetic bg-card" : "border-white/15 hover:border-white/40"}`}
              >
                <FileText size={18} className={method === "invoice" ? "text-kinetic" : "text-zinc-500"} />
                <p className="font-bold uppercase tracking-wide text-sm mt-3">Order Now, Pay Later</p>
                <p className="font-mono text-[11px] text-zinc-500 mt-1 uppercase tracking-widest">We invoice you — 14 day terms</p>
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="border border-white/10 bg-card p-6 lg:sticky lg:top-24" data-testid="order-summary">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-kinetic mb-5">Order summary</p>
            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={i.image} alt="" className="w-14 h-16 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold uppercase truncate">{i.name}</p>
                    <p className="font-mono text-[10px] text-zinc-500 uppercase">{i.size} / {i.color} × {i.qty}</p>
                    {i.customization?.text && <p className="font-mono text-[10px] text-kinetic uppercase">“{i.customization.text}”</p>}
                  </div>
                  <span className="font-mono text-xs text-zinc-300">${(i.unit_price * i.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 mt-5 pt-5 space-y-2 font-mono text-xs uppercase tracking-widest">
              <div className="flex gap-2">
                <input
                  data-testid="promo-code-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="PROMO CODE"
                  className="input-dark font-mono uppercase text-xs tracking-widest !py-2.5"
                />
                <button type="button" data-testid="promo-apply-button" onClick={applyPromo} className="btn-ghost !px-4 !py-2.5">Apply</button>
              </div>
              {promoError && <p className="font-mono text-[11px] text-red-500 uppercase tracking-widest" data-testid="promo-error">{promoError}</p>}
              <div className="flex justify-between text-zinc-400 pt-2"><span>Shipping</span><span className="text-volt">Free</span></div>
              {discount && (
                <div className="flex justify-between text-emerald-500">
                  <span>Discount ({discount.code})</span>
                  <span data-testid="discount-amount">-${discount.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-white text-sm"><span>Total</span><span data-testid="checkout-total">${(total - (discount?.discount_amount || 0)).toFixed(2)}</span></div>
            </div>
            <button
              data-testid="place-order-button"
              type="submit"
              disabled={loading}
              className="btn-kinetic w-full mt-6 disabled:opacity-50"
            >
              {loading ? "Processing…" : method === "card" ? "Pay by card" : "Place order — pay later"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
